import { View, Text, StyleSheet } from 'react-native'
import { Button } from '@/src/common/components/Button'
import { useFocusEffect } from 'expo-router'
import { useLoggedInQuery } from '@/src/common/gql/MesaGql'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loading } from '@/src/common/components/Loading'
import { openInAppBrowser } from '@/src/common/utils/openInAppBrowser'
import { MainChildWrapper } from '@/src/common/wrappers/MainChildWrapper'
import { DesignSystem } from '@/src/design-system'
import { GET_USER_INFO } from '@/src/common/gql/queries/user'
import { DEFAULT_SCREEN_VARIANT } from '@/src/common/modules/MortgageModule'
import { maxWidth } from '@/src/common/wrappers/MainWrapper'
import { fontFamilies, mesaColorNames, typography } from '@/src/common/theme'
import * as Components from '../components'
import * as E2E from '../e2e'
import * as Hooks from '../hooks'
import * as Statsig from '../statsig'

export function MortgageIndex() {
  const [screenVariant, setScreenVariant] =
    useState<Statsig.Store.MortgageModuleVariantName>(DEFAULT_SCREEN_VARIANT)
  const [userPartnerSourceName, setUserPartnerSourceName] = useState<
    Statsig.Store.MortgageModuleVariantName | undefined
  >(undefined)
  const moduleDesign = Statsig.Store.useMortgageModuleDesign(screenVariant)
  const {
    title,
    action_text,
    partner_name,
    action_url,
    image_url,
    details_paragraphs,
    disclosure_paragraphs,
  } = moduleDesign
  const mesaMortgageModuleStateBehavior = Hooks.useMortgageModuleStateBehavior()

  const {
    loading: loadingUserData,
    data: userData,
    refetch: refetchUser,
  } = useLoggedInQuery(GET_USER_INFO, {
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    const sourcePartnerName = userData?.getUserInfo.sourcePartnerName?.toLowerCase()
    if (!sourcePartnerName) {
      setScreenVariant(DEFAULT_SCREEN_VARIANT)
    } else {
      try {
        const parsedSourcePartnerName =
          Statsig.Store.MortgageModuleVariantName.parse(sourcePartnerName)
        setUserPartnerSourceName(parsedSourcePartnerName)
        setScreenVariant(parsedSourcePartnerName)
      } catch (_) {
        // If we can't parse it, that means it's not a partner that drives customers back to them
        // For example, Haven just distributes cards, but does not offer a mortgage product
        setUserPartnerSourceName(undefined)
        setScreenVariant(DEFAULT_SCREEN_VARIANT)
      }
    }
  }, [userData])

  useFocusEffect(
    useCallback(() => {
      void refetchUser()
    }, [refetchUser]),
  )

  const loading =
    mesaMortgageModuleStateBehavior === 'uninitialized' ||
    mesaMortgageModuleStateBehavior === 'loading' ||
    loadingUserData

  const launchMortgageTerms = () => {
    void openInAppBrowser(
      'https://mesamember.com/legal/mesa-membership-terms-and-conditions',
      'Terms and Conditions',
    )
  }

  const handlePrimaryPress = () => {
    if (loading) {
      return
    }
    if (!action_url) {
      return
    }

    void openInAppBrowser(action_url, partner_name || 'Mesa Mortgage')
  }
  const handleSecondaryPress = () => {
    if (loading) {
      return
    }

    if (screenVariant !== DEFAULT_SCREEN_VARIANT) {
      setScreenVariant(DEFAULT_SCREEN_VARIANT)
    } else if (userPartnerSourceName) {
      setScreenVariant(userPartnerSourceName)
    }
  }

  const primaryButtonText = useMemo(() => {
    if (loading) {
      return <Loading size="small" />
    }
    return <Text style={styles.footerButtonText}>{action_text}</Text>
  }, [action_text, loading])

  const secondaryButtonText = useMemo(() => {
    if (loading) {
      return <Loading size="small" />
    }

    const buttonText = (() => {
      switch (screenVariant) {
        case 'prmg':
          return "I don't want to work with PRMG®"
        case 'encompass_lending_group':
          return 'I no longer want to work with Encompass'
        case 'lakeview':
          return "I don't want to speak to a Lakeview loan officer"
        case 'alcova':
          return 'I no longer want to work with ALCOVA'
        case 'carrington':
        case 'default':
          return 'Go back'
        default:
          screenVariant satisfies never
          return 'Go back'
      }
    })()

    return <Text style={[styles.footerButtonText, styles.secondaryButtonText]}>{buttonText}</Text>
  }, [loading, screenVariant])

  if (loadingUserData) {
    return <Loading />
  }

  return (
    <MainChildWrapper title={partner_name || 'Mesa Mortgage'}>
      <DesignSystem.Card style={styles.header} testID={E2E.Config.Ids.View.MortgageIndex}>
        <Components.Header imageUrl={image_url || undefined} />
        <View style={styles.textContainer}>
          <Text style={[typography['heading/heading2'], styles.heading]}>{title}</Text>
          {details_paragraphs &&
            details_paragraphs.map((paragraph) => (
              <Text key={paragraph} style={styles.paragraph}>
                {paragraph}
              </Text>
            ))}
          {screenVariant === DEFAULT_SCREEN_VARIANT && (
            <Text style={styles.terms} testID={E2E.Config.Ids.Text.TermsAndConditions}>
              <Text style={styles.linkStyle} onPress={launchMortgageTerms}>
                Terms and conditions
              </Text>{' '}
              apply.
            </Text>
          )}
        </View>
        <Button
          testID={loading ? '' : E2E.Config.Ids.Press.MarketplaceGetStarted}
          disabled={loading}
          style={[loading ? styles.footerButtonProcessing : styles.footerButtonSubmitMortgageLead]}
          {...(loading
            ? { borderColor: mesaColorNames['Acre Turquoise/500 #00DFC9'], borderWidth: 1 }
            : undefined)}
          onPress={handlePrimaryPress}
          segmentAction="User Started Mortgage Product V2 Flow"
        >
          {primaryButtonText}
        </Button>
        {/* Do not show the "I don't want to work with..." button if we are not licensed in the state 
        or if the partner is Carrington or Lakeview. https://mesamember.slack.com/archives/C08GVE9MPFT/p1761928792422089 */}
        {userPartnerSourceName &&
          !['carrington', 'lakeview'].includes(userPartnerSourceName) &&
          !loading &&
          mesaMortgageModuleStateBehavior.type !== 'hide' && (
            <Button
              testID={E2E.Config.Ids.Press.SwitchDetails}
              style={styles.secondaryButton}
              disabled={loading}
              onPress={handleSecondaryPress}
              segmentAction="User Switched Mortgage Product Details V2 Flow"
            >
              {secondaryButtonText}
            </Button>
          )}
        {disclosure_paragraphs &&
          disclosure_paragraphs.map((paragraph) => (
            <DesignSystem.Markdown
              key={paragraph}
              style={{
                body: {
                  ...styles.disclosureParagraph,
                  color: mesaColorNames['Greyscale/400 #A4AAAC'],
                },
              }}
              children={paragraph}
            />
          ))}
      </DesignSystem.Card>
    </MainChildWrapper>
  )
}

const styles = StyleSheet.create({
  header: {
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
    paddingBottom: 24,
    maxWidth: maxWidth / 2,
  },
  textContainer: { padding: 16, gap: 10 },
  heading: {
    fontFamily: fontFamilies['PlusJakartaSans-Bold'],
    fontWeight: 700,
    fontSize: 24,
    color: mesaColorNames['Greyscale/900 #152438'],
  },
  paragraph: {
    ...typography.S,
    color: mesaColorNames['Greyscale/900 #152438'],
  },
  disclosureParagraph: {
    ...typography.S,
    color: mesaColorNames['Greyscale/400 #A4AAAC'],
    marginTop: 24,
  },
  footerButtonSubmitMortgageLead: {
    backgroundColor: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'],
  },
  footerButtonProcessing: {
    backgroundColor: mesaColorNames['Mesa - Brand/White #FFFFFF'],
  },
  footerButtonProcessingWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  footerButtonText: {
    fontFamily: fontFamilies['PlusJakartaSans-Bold'],
    fontWeight: 700,
    fontSize: 15,
  },
  footerButtonTextProcessing: { color: '#086762' },
  linkStyle: { color: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'] },
  terms: { marginTop: 15 },
  secondaryButton: {
    marginTop: 16,
    color: mesaColorNames['Acre Turquoise/500 #00DFC9'],
    backgroundColor: mesaColorNames['Mesa - Brand/White #FFFFFF'],
    width: '100%',
  },
  secondaryButtonText: {
    color: mesaColorNames['Acre Turquoise/500 #00DFC9'],
  },
})
